import { request } from "@prismicio/editor-support/Http"
import { useOnChange, useStableCallback } from "@prismicio/editor-support/React"
import { useRequest } from "@prismicio/editor-support/Suspense"
import { toURL } from "@prismicio/editor-support/URL"
import {
  Box,
  ErrorBoundary,
  handleEditorFlushSyncError,
  Icon,
  PreviewCard,
  PreviewContent,
  PreviewImage,
  Text,
} from "@prismicio/editor-ui"
import type { EmbedContent } from "@prismicio/types-internal/lib/content"
import { Suspense, useEffect, useRef, useState } from "react"
import { z } from "zod"

import { useEditorConfig } from "../../EditorConfig"
import { EmbedIcon } from "./EmbedIcon"

interface FooterProps {
  debouncedUrl: string
  content: EmbedContent | undefined
  onContentChange: (content?: EmbedContent) => void
  selected?: boolean
}

export function Footer(props: FooterProps) {
  const { debouncedUrl, content, onContentChange, selected } = props

  const errorBoundary = useRef<ErrorBoundary>(null)
  const [hasError, setHasError] = useState(false)

  useOnChange(debouncedUrl, () => {
    if (!hasError) return
    setHasError(false)
    errorBoundary.current?.reset()
  })

  function onError() {
    setHasError(true)
    onContentChange(undefined)
  }

  if (!toURL(debouncedUrl)) return null

  // Avoid refreshing the embed if the URL hasn't changed.
  if (debouncedUrl === content?.embed_url)
    return <Preview content={content} selected={selected} />

  // Fetch a new embed otherwise.
  return (
    <ErrorBoundary
      ref={errorBoundary}
      renderError={renderError}
      onError={onError}
    >
      <Suspense
        fallback={content && <Preview content={content} selected={selected} />}
      >
        <Previewer
          url={debouncedUrl}
          selected={selected}
          onContentChange={onContentChange}
        />
      </Suspense>
    </ErrorBoundary>
  )
}

function renderError() {
  return (
    <Box backgroundColor="grey2" borderRadius={6} padding={16} gap={4}>
      <Icon name="alert" size="small" color="grey11" />
      <Text variant="smallBold" color="grey11">
        There is no embed data available.
      </Text>
    </Box>
  )
}

function Preview(props: { content: EmbedContent; selected?: boolean }) {
  const { content, selected } = props

  const { thumbnail, provider, author, url, subtitle } =
    getPreviewProps(content)

  return (
    <PreviewCard selected={selected}>
      <PreviewImage src={thumbnail.url} aspectRatio={thumbnail.aspectRatio} />
      <PreviewContent>
        <Box display="flex" alignItems="center" gap={4}>
          <EmbedIcon provider={provider} />
          <Text variant="smallBold" color="grey12" noWrap>
            {provider}
          </Text>
          {author && (
            <Text variant="small" color="grey11" noWrap>
              {author}
            </Text>
          )}
        </Box>
        <Text variant="smallBold" href={url}>
          {subtitle}
        </Text>
      </PreviewContent>
    </PreviewCard>
  )
}

interface PreviewerProps {
  url: string
  onContentChange: (content?: EmbedContent) => void
  selected?: boolean
}

function Previewer(props: PreviewerProps) {
  const { url, onContentChange, selected } = props

  const embed = useEmbed({ url, onContentChange })
  return <Preview content={embed} selected={selected} />
}

function useEmbed(params: PreviewerProps) {
  const { url, onContentChange } = params

  const { embedApiEndpoint } = useEditorConfig()
  const embed = useRequest(getEmbed, [url, embedApiEndpoint])
  const stableOnContentChange = useStableCallback(onContentChange)
  useEffect(() => {
    const timer = handleEditorFlushSyncError(() => stableOnContentChange(embed))
    return () => {
      clearTimeout(timer)
    }
  }, [embed, stableOnContentChange])
  return embed
}

function getEmbed(url: string, endpoint: URL) {
  const requestURL = new URL(endpoint)
  requestURL.searchParams.set("url", url)

  return request(requestURL, { okType: createEmbedSchema(url) })
}

function createEmbedSchema(url: string) {
  return z
    .object({
      type: z.string(),
      version: z.string().or(z.number()).nullish(),
      title: z.string().nullish(),
      author_name: z.string().nullish(),
      author_url: z.string().nullish(),
      provider_name: z.string().nullish(),
      provider_url: z.string().nullish(),
      cache_age: z.string().or(z.number()).nullish(),
      thumbnail_url: z.string().nullish(),
      thumbnail_width: z.number().nullish(),
      thumbnail_height: z.number().nullish(),
      html: z.string().nullish(),
    })
    .passthrough()
    .transform((data) => {
      const base = { embed_url: url }
      return {
        ...base,
        __TYPE__: "EmbedContent" as const,
        type: data.type,
        version: data.version,
        title: data.title,
        author_name: data.author_name,
        author_url: data.author_url,
        provider_name: data.provider_name,
        provider_url: data.provider_url,
        cache_age: data.cache_age,
        thumbnail_url: data.thumbnail_url,
        thumbnail_width: data.thumbnail_width,
        thumbnail_height: data.thumbnail_height,
        html: data.html,
        all: { ...base, ...data },
      }
    })
}

function getPreviewProps(content: EmbedContent) {
  let subtitle = ""

  switch (content.type) {
    case "video":
      subtitle = content.title ?? ""
      break
    case "photo":
      subtitle = `View this post${
        content.provider_name ? ` on ${content.provider_name}` : ""
      }`
      break
    default:
      subtitle = `Open${
        content.provider_name ? ` on ${content.provider_name}` : ""
      }`
  }

  return {
    url: content.embed_url,
    thumbnail: {
      url: content.thumbnail_url ?? "",
      aspectRatio:
        content.type === "video" ? ("16:9" as const) : ("1:1" as const),
    },
    provider: content.provider_name ?? "",
    author: content.author_name ?? "",
    subtitle,
  }
}
